/**
 * The durable multi-model (opencode) transcript for a chat.
 *
 * An opencode turn keeps its conversation in a SQLite snapshot in the workspace
 * bucket, not in Firestore. Deleting a chat is otherwise a purely client-side
 * Firestore delete, which cannot reach that snapshot, so the transcript would
 * outlive the chat it belongs to indefinitely.
 *
 * Best effort by design. The caller asked to delete a chat, and losing the chat
 * delete because a storage call failed would be a worse outcome than the orphan
 * it prevents.
 */

const DEFAULT_STREAMING_BASE_URL =
  'https://claude-streaming-service-103376361923.us-central1.run.app';
const CHAT_ID_RE = /^[A-Za-z0-9_-]{1,160}$/;

function streamingBaseUrl(baseUrl) {
  return (
    baseUrl
    || process.env.REACT_APP_STREAMING_SERVICE_URL
    || DEFAULT_STREAMING_BASE_URL
  ).replace(/\/+$/, '');
}

/**
 * @returns {Promise<boolean>} true when the server confirmed the delete. Never
 *   throws: every failure path resolves false so a chat delete can proceed.
 */
export async function deleteOpencodeChatHistory({
  chatId,
  authToken,
  baseUrl,
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  signal,
} = {}) {
  if (!CHAT_ID_RE.test(chatId || '')) return false;
  if (typeof authToken !== 'string' || authToken.length < 1) return false;
  if (typeof fetchImpl !== 'function') return false;

  const url =
    `${streamingBaseUrl(baseUrl)}/chats/${encodeURIComponent(chatId)}/opencode-history`;
  try {
    const response = await fetchImpl(url, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authToken}` },
      ...(signal ? { signal } : {}),
    });
    if (!response?.ok) {
      console.warn(
        '[OpencodeHistory] Server declined the transcript delete',
        chatId,
        response?.status
      );
      return false;
    }
    return true;
  } catch (error) {
    console.warn(
      '[OpencodeHistory] Failed to delete the durable transcript',
      chatId,
      error?.message || String(error)
    );
    return false;
  }
}

export default { deleteOpencodeChatHistory };
